// Session watchdog — proactive JWT refresh on tab focus / app boot.
//
// Fixes the morning-after-reload-everything UX:
//   - User leaves the tab open overnight.
//   - Supabase JWT (1 hour lifetime) expires sometime during the night.
//   - Supabase JS client's built-in auto-refresh sits behind
//     navigator.locks (the same lock UpdateBanner / SupportViewBanner /
//     useActiveTenant contend for); under contention the refresh can
//     hang silently — see memory entry "Auth calls — direct REST
//     bypass for navigator.locks hang".
//   - Result: every Supabase REST call 401s, but error handling swallows
//     the failures, so the UI just renders empty cards / blank lists
//     until the operator manually F5s every page.
//
// Watchdog does two things:
//   1. On app boot and on every `visibilitychange → visible`, check
//      whether the cached session is within 5 minutes of expiry.
//   2. If so, refresh via direct POST to /auth/v1/token (bypassing the
//      lock), write the new session to localStorage, and invalidate
//      every React Query cache so stale views repopulate.
//
// If the refresh itself fails (refresh-token also expired / revoked),
// sign the user out — they'll land on /login and re-authenticate.
//
// Concurrency-safe: a module-level promise gate ensures only one
// refresh is in flight at a time even if multiple tabs / focus events
// fire at once.

import { setSessionRecoverer } from './supabase'
import { signOutEverywhere, readStoredSession } from './offlineSession'
import { refreshSession } from './authRefresh'
import type { QueryClient } from '@tanstack/react-query'

let refreshInFlight: Promise<void> | null = null

// Handle for main.tsx's sleep-detector (idle-wake audit H4): after a ≥90s
// suspend the JWT is often at/near expiry, and firing the invalidate refetch
// burst BEFORE the token heals races every query against a dead bearer
// (benign on a healthy network; feeds a wedge on a bad one). This resolves
// immediately when no refresh is needed, else when the single-flight refresh
// settles — installSessionWatchdog assigns the real runner.
let wakeHealRunner: () => Promise<void> = () => Promise.resolve()
export function healSessionAfterWake(): Promise<void> {
  return wakeHealRunner()
}

function isNearExpiry(expiresAt: number | null | undefined): boolean {
  if (!expiresAt) return false
  const now = Math.floor(Date.now() / 1000)
  return expiresAt - now < 300 // refresh when <5 minutes remain
}

// Refresh the session through the shared single-flight coordinator (the one
// place a manual refresh-token rotation happens — see authRefresh.ts), then
// heal the UI. The coordinator owns the bounded fetch, the rotation guard, and
// persistence; this wrapper only decides what to do with the outcome.
async function refreshAndHeal(qc: QueryClient): Promise<void> {
  const r = await refreshSession()
  if (r.ok) {
    // New (or adopted) tokens are in localStorage + in-memory. Refetch so the
    // empty / anon-failed cards repopulate with the fresh JWT.
    //
    // FIRE-AND-FORGET, never `await` (2026-06-25): this runner is also the
    // anon-guard's sessionRecoverer (supabase.ts:166 awaits it before issuing a
    // mid-session REST call that resolved as anon). invalidateQueries() doesn't
    // resolve until it has refetched EVERY active query — including the very
    // query that triggered the recoverer and is still parked on that await. That
    // closed the loop: queryFn → sessionRecoverer() → invalidateQueries() → same
    // queryFn, so nothing settled and a freshly-mounted detail *route* spun on a
    // silent full-page spinner until reload (token DID refresh, zero failed
    // calls, zero errors — the operators' "drill in after a while → hangs"
    // report, reproduced 2/2 on /deliveries/:id + /growers/:id). The token heal
    // is already done by refreshSession() above; the invalidate is a pure UI
    // heal no caller awaits, so let it run async and break the cycle.
    void qc.invalidateQueries()
    return
  }
  // Sign out ONLY on a definitive revocation (GoTrue rejected the token). A
  // network/no-session/bad-response result leaves the (possibly-still-valid)
  // session alone — the next boot/visibility/heartbeat check retries. Do NOT
  // signOut on a navigator.onLine hint: it false-reports `true` on weak links +
  // captive portals and would clear a valid field session.
  if (r.reason === 'revoked') await signOutEverywhere()
}

export function installSessionWatchdog(qc: QueryClient): void {
  // Single-flight runner shared by check() below and the anon-fallback fetch
  // guard in supabase.ts (which calls it via setSessionRecoverer when a REST
  // request is about to go out unauthenticated mid-session).
  const runRefresh = (): Promise<void> => {
    if (!refreshInFlight) {
      refreshInFlight = refreshAndHeal(qc)
        .catch(e => { console.warn('sessionWatchdog: refresh threw', e) })
        .finally(() => { refreshInFlight = null })
    }
    return refreshInFlight
  }

  // Flat guard sequence; returning the in-flight promise (so the sleep-wake
  // heal can order itself after it) adds branches the gate counts but no
  // real complexity. Check scripts carry no coverage, so CRAP flags it.
  // fallow-ignore-next-line complexity
  const check = (): Promise<void> => {
    if (refreshInFlight) return refreshInFlight
    // Read expiry straight from localStorage — NOT supabase.auth.getSession().
    // getSession() resolves the token through auth-js's in-process lock, and on
    // an expired token it triggers the refresh that can wedge that lock (dead
    // socket after sleep). Gating the recovery path on the very call it exists
    // to recover from is why the watchdog couldn't self-heal an already-wedged
    // client (confirmed 2026-06-14: getSession() never resolved, so check()
    // never reached runRefresh). readStoredSession() is a synchronous
    // localStorage read that can never block, and is the source of truth anyway.
    const stored = readStoredSession()
    if (!stored?.refresh_token) return Promise.resolve()   // genuinely signed out — App.tsx routes to /login
    // Refresh when the stored token is at/near expiry, OR when no expiry is
    // recorded (a garbled/partial session still gets healed rather than wedged).
    if (stored.expires_at && !isNearExpiry(stored.expires_at)) return Promise.resolve()
    return runRefresh()
  }

  setSessionRecoverer(runRefresh)
  wakeHealRunner = check

  // Boot — catches a fresh page-load that rehydrated an already-expired session
  // before any queries fire.
  void check()

  // Tab focus — catches the "left tab open overnight, come back next morning"
  // case where supabase-js's background auto-refresh hung on navigator.locks.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void check()
  })

  // Continuous 60s heartbeat — catches the in-tab case where the user stays on
  // the same tab without focus change for >1 hour. supabase-js's background
  // auto-refresh CAN hang silently on navigator.locks contention from the
  // many useQuery hooks scattered across the app; when that happens the
  // in-memory JWT goes stale, every Supabase REST call 401s, our global
  // error handler swallows the failures, and the UI just renders empty
  // until the operator manually reloads. The heartbeat is a safety net that
  // forces a direct-REST refresh well before the cached expiry hits.
  //
  // 60s cadence × 5-min refresh window means we'll fire 4-5 checks during
  // the "should refresh" zone — comfortably within the cache-warm window
  // for any single refresh attempt to land.
  setInterval(() => { void check() }, 60_000)
}
